import TableOfContents, {
    type GetTableOfContentIndexFunction,
    type GetTableOfContentLevelFunction,
    type TableOfContentData,
    type TableOfContentDataItem,
} from '@tiptap/extension-table-of-contents';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Transaction } from '@tiptap/pm/state';
import { Plugin, PluginKey } from '@tiptap/pm/state';

export type { GetTableOfContentIndexFunction, GetTableOfContentLevelFunction, TableOfContentData, TableOfContentDataItem };

export type ZyTableOfContentsOptions = {
    resolveAnchorId?: (node: PMNode) => string | null;
    anchorTypes?: string[];
    onUpdate?: (data: TableOfContentData, isCreate?: boolean) => void;
    getLevel?: GetTableOfContentLevelFunction;
    getIndex?: GetTableOfContentIndexFunction;
    getId?: (textContent: string) => string;
    scrollParent?: (() => HTMLElement | Window) | HTMLElement | Window;
};

function defaultResolveAnchorId(node: PMNode): string | null {
    const id = node.attrs.id;
    return typeof id === 'string' && id.length > 0 ? id : null;
}

type SyncAnchorIdsOptions = {
    tr: Transaction;
    doc: PMNode;
    anchorTypes: string[];
    resolveAnchorId: (node: PMNode) => string | null;
    getId: (textContent: string) => string;
};

function syncAnchorIds({ tr, doc, anchorTypes, resolveAnchorId, getId }: SyncAnchorIdsOptions): boolean {
    let modified = false;
    const existingIds: string[] = [];

    doc.descendants((node, pos) => {
        const nodeId = node.attrs['data-toc-id'] as string | null | undefined;
        if (!anchorTypes.includes(node.type.name) || node.textContent.length === 0) {
            return;
        }

        const resolvedId = resolveAnchorId(node);
        if (resolvedId) {
            if (nodeId !== resolvedId) {
                tr.setNodeMarkup(pos, undefined, { ...node.attrs, 'data-toc-id': resolvedId });
                modified = true;
            }
            existingIds.push(resolvedId);
            return;
        }

        if (nodeId === null || nodeId === undefined || existingIds.includes(nodeId)) {
            const fallbackId = getId(node.textContent);
            const attrsId = node.attrs.id;
            const hasExistingId = typeof attrsId === 'string' && attrsId.length > 0;
            tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                'data-toc-id': fallbackId,
                ...(hasExistingId ? {} : { id: fallbackId }),
            });
            modified = true;
            existingIds.push(fallbackId);
            return;
        }

        existingIds.push(nodeId);
    });

    return modified;
}

const zyTableOfContentsAnchorIdsKey = new PluginKey('zyTableOfContentsAnchorIds');

function createZyTableOfContentsAnchorIdsPlugin(options: {
    anchorTypes: string[];
    resolveAnchorId: (node: PMNode) => string | null;
    getId: (textContent: string) => string;
}) {
    const { anchorTypes, resolveAnchorId, getId } = options;

    return new Plugin({
        key: zyTableOfContentsAnchorIdsKey,
        appendTransaction(transactions, _oldState, newState) {
            if (typeof window === 'undefined') {
                return null;
            }
            if (transactions.some(tr => tr.getMeta('composition'))) {
                return null;
            }
            if (!transactions.some(transaction => transaction.docChanged)) {
                return null;
            }

            const tr = newState.tr;
            const modified = syncAnchorIds({
                tr,
                doc: newState.doc,
                anchorTypes,
                resolveAnchorId,
                getId,
            });

            return modified ? tr : null;
        },
    });
}

export const ZyTableOfContents = TableOfContents.extend<ZyTableOfContentsOptions>({
    addOptions() {
        return {
            ...this.parent?.(),
            resolveAnchorId: defaultResolveAnchorId,
        };
    },

    addProseMirrorPlugins() {
        const anchorTypes = this.options.anchorTypes ?? ['heading'];
        const resolveAnchorId = this.options.resolveAnchorId ?? defaultResolveAnchorId;
        const getId = this.options.getId ?? (() => crypto.randomUUID());

        return [
            createZyTableOfContentsAnchorIdsPlugin({
                anchorTypes,
                resolveAnchorId,
                getId,
            }),
        ];
    },

    onCreate(props) {
        if (typeof window === 'undefined' || !this.editor.view) {
            return;
        }

        const anchorTypes = this.options.anchorTypes ?? ['heading'];
        const resolveAnchorId = this.options.resolveAnchorId ?? defaultResolveAnchorId;
        const getId = this.options.getId ?? (() => crypto.randomUUID());

        const tr = this.editor.state.tr;
        const modified = syncAnchorIds({
            tr,
            doc: this.editor.state.doc,
            anchorTypes,
            resolveAnchorId,
            getId,
        });
        if (modified) {
            this.editor.view.dispatch(tr);
        }

        this.parent?.(props);
    },
});

export default ZyTableOfContents;
